import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Stack,
  Skeleton,
  IconButton,
  alpha,
  useTheme,
  Tooltip,
  Paper,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "react-query";
import { useEffectivePermissions, scopeSetFromPermissions } from "../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../context/AuthContext";
import { useTeamBoardsList, useTeamBoard, useTeamBoardMutations, TEAM_BOARDS_KEY } from "../../hooks/useTeamBoards";
import { updateTeamBoard, deleteTeamBoard } from "../../api/client";
import { canManageTeamBoard, canManageBoardMembers } from "./utils/teamBoardPermissions";
import { collectBoardAssigneeOptions, collectBoardClientOptions } from "./utils/teamBoardCardFilters";
import { tPlural } from "./utils/tPlural";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import { openTeamTasksPopupWindow } from "../../utils/openTeamTasksPopupWindow";
import { TEAM_BASE } from "../../utils/teamNavigation";
import { TEAM_MOBILE_MEDIA_QUERY } from "../Team/utils/teamUiUtils";
import BoardListCard from "./components/BoardListCard";
import BoardMembersDialog from "./components/BoardMembersDialog";
import BoardAnalyticsDialog from "./components/BoardAnalyticsDialog";
import BoardSummaryTasksDialog from "./components/BoardSummaryTasksDialog";
import TaskCardModalContainer from "./components/TaskCardModalContainer";
import BoardFormDialog from "./components/BoardFormDialog";
import EmptyStateTeamBoards from "./components/EmptyStateTeamBoards";
import TeamTasksLoadError from "./components/TeamTasksLoadError";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { TeamTasksFeedbackProvider } from "./components/TeamTasksFeedbackProvider";
import { useTeamBoardAction } from "./hooks/useTeamBoardAction";

function BoardListSkeleton() {
  return (
    <Grid container spacing={2.5}>
      {[0, 1, 2].map((i) => (
        <Grid item xs={12} sm={6} lg={4} key={i}>
          <Skeleton variant="rounded" height={240} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
  );
}

function SummaryStat({ icon, label, tone = "default", compact = false, onClick, ariaLabel }) {
  const theme = useTheme();
  const palette =
    tone === "warning"
      ? theme.palette.warning
      : tone === "primary"
        ? theme.palette.primary
        : null;
  const clickable = typeof onClick === "function";

  return (
    <Stack
      component={clickable ? "button" : "div"}
      type={clickable ? "button" : undefined}
      onClick={onClick}
      aria-label={ariaLabel}
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: compact ? 1 : 1.5,
        py: compact ? 0.75 : 1,
        borderRadius: 2,
        border: 0,
        textAlign: "left",
        width: "100%",
        cursor: clickable ? "pointer" : "default",
        bgcolor: palette
          ? alpha(palette.main, theme.palette.mode === "dark" ? 0.12 : 0.08)
          : alpha(theme.palette.action.active, 0.06),
        color: "inherit",
        font: "inherit",
        minWidth: 0,
        height: "100%",
        transition: "background-color 0.15s ease",
        "&:hover": clickable
          ? {
              bgcolor: palette
                ? alpha(palette.main, theme.palette.mode === "dark" ? 0.22 : 0.16)
                : alpha(theme.palette.action.active, 0.12),
            }
          : undefined,
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ color: palette ? `${tone}.main` : "text.secondary", display: "flex", flexShrink: 0 }}>
        {icon}
      </Box>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{
          fontSize: compact ? "0.72rem" : undefined,
          lineHeight: 1.25,
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

const BoardListPageInner = ({ standalone = false, basePath = "/equipe/tarefas" }) => {
  const { t } = useTranslation("teamTasks");
  const theme = useTheme();
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { run } = useTeamBoardAction();
  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const scopes = scopeSetFromPermissions(permissionsData, userData);
  const { data: boards, isLoading, isError, error, refetch, isFetching } = useTeamBoardsList({
    workspace: true,
  });
  const { createBoard } = useTeamBoardMutations(null);

  const updateBoardMutation = useMutation(
    ({ id, ...payload }) => updateTeamBoard(id, payload),
    { onSuccess: () => queryClient.invalidateQueries(TEAM_BOARDS_KEY) }
  );
  const deleteBoardMutation = useMutation(
    (id) => deleteTeamBoard(id),
    { onSuccess: () => queryClient.invalidateQueries(TEAM_BOARDS_KEY) }
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [editBoard, setEditBoard] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [membersBoard, setMembersBoard] = useState(null);
  const [analyticsBoard, setAnalyticsBoard] = useState(null);
  const [summaryKind, setSummaryKind] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const membersBoardId = membersBoard?.id || null;
  const analyticsBoardId = analyticsBoard?.id || null;
  const { data: membersBoardDetail } = useTeamBoard(membersBoardId, {
    enabled: Boolean(membersBoardId),
  });
  const { data: analyticsBoardDetail } = useTeamBoard(analyticsBoardId, {
    enabled: Boolean(analyticsBoardId),
  });
  const memberMutations = useTeamBoardMutations(membersBoardId);
  const membersSource = membersBoardDetail || membersBoard;
  const analyticsSource = analyticsBoardDetail || analyticsBoard;

  const boardList = boards || [];
  const teamBasePath = useMemo(
    () => basePath.replace(/\/tarefas\/?$/, "") || TEAM_BASE,
    [basePath]
  );
  const isEmpty = !isLoading && !isError && boardList.length === 0;
  const hasBoards = !isLoading && !isError && boardList.length > 0;
  const showOpenInPopup = !standalone && !isMobile;
  const showBackToTeam = !isMobile || standalone;

  const totals = useMemo(() => {
    return boardList.reduce(
      (acc, board) => {
        const stats = board.stats || {};
        acc.openTasks += stats.openCardCount || 0;
        acc.overdueTasks += stats.overdueCount || 0;
        acc.unassignedTasks += stats.unassignedCount || 0;
        acc.totalTasks += stats.cardCount || 0;
        acc.members += board.memberCount || board.members?.length || 0;
        return acc;
      },
      { openTasks: 0, overdueTasks: 0, unassignedTasks: 0, totalTasks: 0, members: 0 }
    );
  }, [boardList]);

  const handleCreate = async (payload) => {
    const board = await run(
      () => createBoard.mutateAsync(payload),
      { context: "createBoard", successKey: "successBoardCreated" }
    );
    if (board?.id) {
      navigate(`${basePath}/${board.id}`);
      return true;
    }
    return false;
  };

  const handleEdit = async (payload) => {
    if (!editBoard?.id) return false;
    const result = await run(
      () => updateBoardMutation.mutateAsync({ id: editBoard.id, ...payload }),
      { context: "updateBoard", successKey: "successBoardSaved" }
    );
    if (result !== null) {
      setEditBoard(null);
      return true;
    }
    return false;
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    const result = await run(
      () => deleteBoardMutation.mutateAsync(deleteTarget.id),
      { context: "deleteBoard", successKey: "successBoardDeleted" }
    );
    if (result !== null) setDeleteTarget(null);
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: alpha(theme.palette.background.default, 0.4),
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        ...(standalone
          ? {
              height: "100vh",
              overflow: "auto",
            }
          : {}),
      }}
    >
      {standalone ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 2,
            pt: 1.25,
            flexShrink: 0,
          }}
        >
          <Tooltip title={t("closeWindow")}>
            <IconButton size="small" onClick={() => window.close()} aria-label={t("closeWindow")}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}

      <Box
        sx={{
          p: { xs: 1.5, sm: 2, md: 2.5 },
          pb: { xs: 3, sm: 4 },
          maxWidth: 1280,
          mx: "auto",
          flex: 1,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.25, sm: 2, md: 2.5 },
            mb: { xs: 1.5, sm: 2 },
            borderRadius: { xs: 2, sm: 3 },
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.14 : 0.07)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 55%)`,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <Stack
            direction={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "stretch", lg: "flex-start" }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ width: "100%", minWidth: 0 }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 44 },
                  height: { xs: 36, sm: 44 },
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.14),
                  flexShrink: 0,
                }}
              >
                <ViewKanbanOutlinedIcon color="primary" fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  component="h1"
                  sx={{ fontWeight: 800, lineHeight: 1.2 }}
                >
                  {t("boardListTitle")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    lineHeight: 1.55,
                    maxWidth: 520,
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {t("boardListIntro")}
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              sx={{
                width: { xs: "100%", lg: "auto" },
                minWidth: 0,
                flexShrink: 0,
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                "& .MuiButton-root": {
                  flex: { xs: "1 1 calc(50% - 4px)", sm: "0 1 auto" },
                  minWidth: 0,
                  textTransform: "none",
                  fontWeight: 600,
                },
              }}
            >
              {showBackToTeam ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(teamBasePath)}
                >
                  {t("backToTeam")}
                </Button>
              ) : null}
              <Button
                variant="outlined"
                size="small"
                startIcon={<AssignmentOutlinedIcon />}
                onClick={() => navigate(`${basePath}/inbox`)}
              >
                {t("inboxOpenFromBoardList")}
              </Button>
              {isMobile ? (
                <Tooltip title={t("refreshList")}>
                  <IconButton
                    size="small"
                    onClick={() => refetch()}
                    disabled={isLoading || isFetching}
                    aria-label={t("refreshList")}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1.5,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                  disabled={isLoading || isFetching}
                >
                  {t("refreshList")}
                </Button>
              )}
              {showOpenInPopup ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewOutlinedIcon />}
                  onClick={() => openTeamTasksPopupWindow()}
                >
                  {t("openInPopup")}
                </Button>
              ) : null}
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setCreateOpen(true)}
                sx={{
                  fontWeight: 700,
                  px: 2,
                  flex: { xs: "1 1 100%", sm: "0 1 auto" },
                }}
              >
                {t("createBoard")}
              </Button>
            </Stack>
          </Stack>

          {hasBoards ? (
            <Box
              sx={{
                mt: { xs: 1.25, sm: 2 },
                display: "grid",
                gap: 1,
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              <SummaryStat
                icon={<ViewKanbanOutlinedIcon fontSize="small" />}
                label={tPlural(t, "boardCount", boardList.length)}
                tone="primary"
                compact={isMobile}
              />
              <SummaryStat
                icon={<AssignmentOutlinedIcon fontSize="small" />}
                label={tPlural(t, "boardListSummaryOpen", totals.openTasks)}
                tone="primary"
                compact={isMobile}
                onClick={() => setSummaryKind("open")}
                ariaLabel={t("boardListSummaryTasksOpenAria")}
              />
              <SummaryStat
                icon={<AssignmentOutlinedIcon fontSize="small" />}
                label={tPlural(t, "boardListSummaryTotalTasks", totals.totalTasks)}
                compact={isMobile}
                onClick={() => setSummaryKind("all")}
                ariaLabel={t("boardListSummaryTasksTotalAria")}
              />
              <SummaryStat
                icon={<PersonOffOutlinedIcon fontSize="small" />}
                label={
                  totals.unassignedTasks > 0
                    ? tPlural(t, "boardListSummaryUnassigned", totals.unassignedTasks)
                    : t("boardListSummaryNoUnassigned")
                }
                compact={isMobile}
                onClick={() => setSummaryKind("unassigned")}
                ariaLabel={t("boardListSummaryTasksUnassignedAria")}
              />
              <SummaryStat
                icon={<WarningAmberOutlinedIcon fontSize="small" />}
                label={
                  totals.overdueTasks > 0
                    ? tPlural(t, "boardListSummaryOverdue", totals.overdueTasks)
                    : t("boardListSummaryNoOverdue")
                }
                tone={totals.overdueTasks > 0 ? "warning" : "default"}
                compact={isMobile}
                onClick={() => setSummaryKind("overdue")}
                ariaLabel={t("boardListSummaryTasksOverdueAria")}
              />
            </Box>
          ) : null}
        </Paper>

        {isError && (
          <Box sx={{ mb: 3 }}>
            <TeamTasksLoadError error={error} onRetry={() => refetch()} isRetrying={isFetching} />
          </Box>
        )}

        {isLoading && <BoardListSkeleton />}
        {isEmpty && <EmptyStateTeamBoards onCreateClick={() => setCreateOpen(true)} />}

        {hasBoards && (
          <>
            <Stack spacing={0.25} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {t("boardListSectionTitle")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                {t("boardListSectionBody")}
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {boardList.map((board) => (
                <Grid item xs={12} sm={6} lg={4} key={board.id}>
                  <BoardListCard
                    board={board}
                    canManage={canManageTeamBoard(scopes, board)}
                    onOpen={(b) => navigate(`${basePath}/${b.id}`)}
                    onOpenPopup={showOpenInPopup ? (b) => openTeamTasksPopupWindow({ boardId: b.id }) : undefined}
                    onOpenMembers={setMembersBoard}
                    onOpenAnalytics={setAnalyticsBoard}
                    onOrganizeColumns={(b) => navigate(`${basePath}/${b.id}?organize=1`)}
                    onEdit={setEditBoard}
                    onDelete={setDeleteTarget}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}

        <BoardFormDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreate}
          isLoading={createBoard.isLoading}
          mode="create"
        />

        <BoardFormDialog
          open={Boolean(editBoard)}
          onClose={() => setEditBoard(null)}
          onSubmit={handleEdit}
          isLoading={updateBoardMutation.isLoading}
          mode="edit"
          initialValues={{
            name: editBoard?.name || "",
            description: editBoard?.description || "",
          }}
        />

        <BoardMembersDialog
          open={Boolean(membersBoard)}
          onClose={() => setMembersBoard(null)}
          board={membersSource}
          members={membersSource?.members || []}
          currentUserId={userData?.id}
          currentUserRole={membersSource?.currentUserRole || membersBoard?.currentUserRole || null}
          canManageMembers={canManageBoardMembers(scopes, membersSource || membersBoard)}
          isBusy={
            memberMutations.addMember.isLoading ||
            memberMutations.removeMember.isLoading ||
            memberMutations.updateMemberRole.isLoading
          }
          onAddMember={(payload) =>
            run(() => memberMutations.addMember.mutateAsync(payload), {
              context: "addMember",
              successKey: "successMemberAdded",
            })
          }
          onUpdateMemberRole={(memberId, role) =>
            run(() => memberMutations.updateMemberRole.mutateAsync({ memberId, role }), {
              context: "updateMemberRole",
              successKey: "successMemberRoleUpdated",
            })
          }
          onRemoveMember={(memberId) =>
            run(() => memberMutations.removeMember.mutateAsync(memberId), {
              context: "removeMember",
              successKey: "successMemberRemoved",
            })
          }
        />

        <BoardAnalyticsDialog
          open={Boolean(analyticsBoard)}
          onClose={() => setAnalyticsBoard(null)}
          boardId={analyticsBoardId}
          boardName={analyticsSource?.name || analyticsBoard?.name}
          assigneeOptions={collectBoardAssigneeOptions(
            analyticsSource?.columns || [],
            analyticsSource?.members || [],
            []
          )}
          clientOptions={collectBoardClientOptions(analyticsSource?.columns || [], analyticsSource)}
          columns={analyticsSource?.columns || []}
        />

        <BoardSummaryTasksDialog
          open={Boolean(summaryKind)}
          kind={summaryKind}
          onClose={() => setSummaryKind(null)}
          onOpenTask={setSelectedTask}
        />

        <TaskCardModalContainer
          open={Boolean(selectedTask)}
          card={selectedTask}
          boardId={selectedTask?.boardId}
          onClose={() => setSelectedTask(null)}
          invalidateInboxOnClose
        />

        <ConfirmDialog
          open={Boolean(deleteTarget)}
          title={t("deleteBoardTitle")}
          content={t("deleteBoardBody")}
          confirmText={t("deleteBoardConfirm")}
          cancelText={t("cancel")}
          onClose={() => !deleteBoardMutation.isLoading && setDeleteTarget(null)}
          onConfirm={handleDelete}
          loading={deleteBoardMutation.isLoading}
        />
      </Box>
    </Box>
  );
};

const BoardListPage = (props) => (
  <TeamTasksFeedbackProvider>
    <BoardListPageInner {...props} />
  </TeamTasksFeedbackProvider>
);

export default BoardListPage;
